import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { MessageSquare, Plus, Pencil, Trash2, Users, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhatsAppTemplates() {
  const { token, user } = useAuth();
  const isAdmin = ['super_admin', 'admin_ops'].includes(user?.role);
  const { platformSettings: pSettings } = useSettings();
  const [templates, setTemplates] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', assigned_to: [] });

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp-templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data || []);
    } catch { toast.error('Erreur de chargement'); } finally { setLoading(false); }
  };

  const fetchAnalysts = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/whatsapp-templates/analysts`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalysts(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchTemplates(); fetchAnalysts(); }, [token]);

  const openNew = () => {
    setEditTpl(null);
    setForm({ title: '', body: 'Bonjour Monsieur {NOM},\n\n\n\n{SIGNATURE}', assigned_to: [] });
    setEditOpen(true);
  };

  const openEdit = (tpl) => {
    setEditTpl(tpl);
    setForm({ title: tpl.title, body: tpl.body, assigned_to: tpl.assigned_to || [] });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Titre et contenu requis'); return; }
    try {
      const payload = { title: form.title, body: form.body };
      if (isAdmin) payload.assigned_to = form.assigned_to.length > 0 ? form.assigned_to : null;
      if (editTpl) {
        await axios.put(`${API}/whatsapp-templates/${editTpl.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Template modifié');
      } else {
        await axios.post(`${API}/whatsapp-templates`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Template créé');
      }
      setEditOpen(false);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Supprimer "${tpl.title}" ?`)) return;
    try {
      await axios.delete(`${API}/whatsapp-templates/${tpl.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Supprimé');
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const toggleAssign = (analystId) => {
    setForm(prev => {
      const current = prev.assigned_to || [];
      if (current.includes('all')) return { ...prev, assigned_to: [analystId] };
      if (current.includes(analystId)) return { ...prev, assigned_to: current.filter(id => id !== analystId) };
      return { ...prev, assigned_to: [...current, analystId] };
    });
  };

  const toggleAssignAll = () => {
    setForm(prev => ({
      ...prev,
      assigned_to: (prev.assigned_to || []).includes('all') ? [] : ['all']
    }));
  };

  const previewMsg = (body) => {
    const platformName = pSettings?.platform_name || 'La Plateforme';
    return body.replace(/\{NOM\}/g, 'DUPONT').replace(/\{SIGNATURE\}/g, `${user?.first_name || 'Jean'} ${user?.last_name || 'Analyste'} - ${platformName}`).replace(/\{DATE\}/g, '15/01/2026').replace(/\{HEURE\}/g, '14h00');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="whatsapp-templates-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-green-500" />
              Templates WhatsApp
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isAdmin ? 'Gérez les templates de tous les analystes' : 'Gérez vos templates de messages WhatsApp'}
            </p>
          </div>
          <Button onClick={openNew} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau template
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Aucun template</p>
              <Button className="mt-4" variant="outline" onClick={openNew}>Créer un template</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((tpl) => (
              <Card key={tpl.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${tpl.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{tpl.title}</CardTitle>
                    <div className="flex items-center gap-1">
                      {tpl.is_default && <Badge variant="outline" className="text-[10px]">Défaut</Badge>}
                    </div>
                  </div>
                  {isAdmin && tpl.owner_name && (
                    <div className="flex items-center gap-1 mt-1">
                      <User className="w-3 h-3 text-muted-foreground" />
                      <span className="text-[10px] text-muted-foreground">{tpl.owner_name}</span>
                    </div>
                  )}
                  {isAdmin && tpl.assigned_to && tpl.assigned_to.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Users className="w-3 h-3 text-blue-500" />
                      <span className="text-[10px] text-blue-500">
                        {tpl.assigned_to.includes('all') ? 'Tous les analystes' : `${tpl.assigned_to.length} analyste(s)`}
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground whitespace-pre-line line-clamp-5 mb-4">{previewMsg(tpl.body)}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(tpl)} data-testid={`edit-template-${tpl.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(tpl)} data-testid={`delete-template-${tpl.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Variables help */}
        <Card>
          <CardContent className="py-4">
            <p className="text-sm font-medium mb-2">Variables disponibles</p>
            <div className="flex flex-wrap gap-2">
              {[{v:'{NOM}',d:'Nom de famille du client (MAJUSCULES)'},{v:'{SIGNATURE}',d:'Prénom Nom Analyste - Nom Plateforme'},{v:'{DATE}',d:'Date du RDV (éditable avant envoi)'},{v:'{HEURE}',d:'Heure du RDV (éditable avant envoi)'}].map(v => (
                <Badge key={v.v} variant="secondary" className="text-xs font-mono cursor-default" title={v.d}>{v.v}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editTpl ? 'Modifier le template' : 'Nouveau template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Ex: Confirmation de RDV" className="mt-1" data-testid="tpl-title-input" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={form.body} onChange={(e) => setForm(p => ({...p, body: e.target.value}))} rows={8} className="mt-1 font-mono text-sm" placeholder="Bonjour Monsieur {NOM},..." data-testid="tpl-body-input" />
            </div>

            {/* Admin only: assign to analysts */}
            {isAdmin && (
              <div>
                <Label>Assigner aux analystes</Label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={(form.assigned_to || []).includes('all')} onChange={toggleAssignAll} className="rounded" />
                    <span className="text-sm font-medium">Tous les analystes</span>
                  </label>
                  {!(form.assigned_to || []).includes('all') && analysts.map(a => (
                    <label key={a.id} className="flex items-center gap-2 cursor-pointer ml-4">
                      <input type="checkbox" checked={(form.assigned_to || []).includes(a.id)} onChange={() => toggleAssign(a.id)} className="rounded" />
                      <span className="text-sm">{a.first_name} {a.last_name} <span className="text-muted-foreground text-xs">({a.email})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Preview */}
            <div>
              <Label className="text-muted-foreground">Aperçu</Label>
              <div className="mt-1 p-3 rounded-lg bg-green-950/20 border border-green-900/30 text-sm whitespace-pre-line">
                {previewMsg(form.body)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="save-template-btn">{editTpl ? 'Enregistrer' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
